import ROUTES from '../assets/routes'
import { Exchange, exchangeFromJSON } from '../proto/generated/common'
import { Security } from '../proto/generated/security'
import { ShareholdingDetail } from '../proto/generated/shareholding'
import { formatHolderDisplayName, formatShareholdingPeriodLabel } from './shareholdingData'
import { resolveSecurityBySymbol } from './security'

export const SHAREHOLDER_HOLDER_QUERY_PARAM = 'holder'
export const SHAREHOLDER_HOLDER_EXACT_QUERY_PARAM = 'holderExact'
export const SHAREHOLDER_PORTFOLIO_SECTION_PARAM = 'section'

export type ShareholderPortfolioSection = 'shareholding' | 'bulk-block-deals'

export const SHAREHOLDER_PORTFOLIO_SECTION_ITEMS: Array<{ key: ShareholderPortfolioSection; label: string }> = [
    { key: 'shareholding', label: 'Shareholding' },
    { key: 'bulk-block-deals', label: 'Bulk & Block Deals' },
]

export const parseShareholderPortfolioSection = (value: string | null): ShareholderPortfolioSection =>
    value === 'bulk-block-deals' ? 'bulk-block-deals' : 'shareholding'

export const resolveShareholderPortfolioSectionIndex = (section: ShareholderPortfolioSection): number =>
    section === 'bulk-block-deals' ? 1 : 0
export const LATEST_SHAREHOLDER_QUARTER_VALUE = '__latest__'
export const SHAREHOLDER_SEARCH_ROW_LIMIT = 200
export const SHAREHOLDER_SEARCH_TRUNCATION_MESSAGE =
    'Results truncated because your query was too generic or resulted in more than 200 results, try choosing a longer name.'

const SHAREHOLDER_QUARTER_ENDS = [
    { month: 12, day: 31 },
    { month: 9, day: 30 },
    { month: 6, day: 30 },
    { month: 3, day: 31 },
] as const

const OLDEST_SHAREHOLDER_QUARTER = { year: 2016, month: 3, day: 31 }

export type ShareholderSearchQuarterOption = {
    value: string
    label: string
}

export const getShareholderSearchQuarterOptions = (): ShareholderSearchQuarterOption[] => {
    const options: ShareholderSearchQuarterOption[] = []
    const today = new Date()
    today.setHours(12, 0, 0, 0)
    const oldestQuarter = new Date(
        OLDEST_SHAREHOLDER_QUARTER.year,
        OLDEST_SHAREHOLDER_QUARTER.month - 1,
        OLDEST_SHAREHOLDER_QUARTER.day,
        12,
        0,
        0,
        0,
    )

    for (let year = today.getFullYear(); year >= OLDEST_SHAREHOLDER_QUARTER.year; year -= 1) {
        for (const quarterEnd of SHAREHOLDER_QUARTER_ENDS) {
            const endDate = new Date(year, quarterEnd.month - 1, quarterEnd.day, 12, 0, 0, 0)
            if (endDate > today || endDate < oldestQuarter) {
                continue
            }
            const value = `${year}-${String(quarterEnd.month).padStart(2, '0')}-${String(quarterEnd.day).padStart(2, '0')}`
            options.push({
                value,
                label: formatShareholdingPeriodLabel(value),
            })
        }
    }

    return options
}

const registerResolvedSecurity = (
    bySymbol: Record<string, Security>,
    requestedSymbol: string,
    security: Security,
) => {
    const keys = new Set(
        [
            requestedSymbol,
            requestedSymbol.toUpperCase(),
            security.code,
            security.code.toUpperCase(),
            security.symbol,
            security.symbol?.toUpperCase(),
            security.exchangeSymbol,
            security.exchangeSymbol?.toUpperCase(),
        ].filter(Boolean) as string[],
    )
    keys.forEach((key) => {
        bySymbol[key] = security
    })
}

export const getShareholderResolvedSecurity = (
    securitiesBySymbol: Record<string, Security>,
    symbol: string,
): Security | undefined => {
    const trimmed = symbol.trim()
    return securitiesBySymbol[trimmed] ?? securitiesBySymbol[trimmed.toUpperCase()]
}

export const resolveShareholderSecurities = async (
    rows: ShareholderPortfolioRow[],
): Promise<Record<string, Security>> => {
    const bySymbol: Record<string, Security> = {}
    const symbolsToResolve = new Map<string, Exchange>()

    rows.forEach((row) => {
        const symbol = row.symbol.trim()
        if (!symbol || symbolsToResolve.has(symbol)) {
            return
        }
        symbolsToResolve.set(symbol, row.exchange)
    })

    await Promise.all(
        [...symbolsToResolve.entries()].map(async ([symbol, exchange]) => {
            const security = await resolveSecurityBySymbol(exchange, symbol)
            if (security) {
                registerResolvedSecurity(bySymbol, symbol, security)
            }
        }),
    )

    return bySymbol
}

export const resolveShareholderRowSecurity = async (
    row: ShareholderPortfolioRow,
    securitiesBySymbol: Record<string, Security>,
): Promise<Security | undefined> => {
    const cached = getShareholderResolvedSecurity(securitiesBySymbol, row.symbol)
    return cached ?? (await resolveSecurityBySymbol(row.exchange, row.symbol))
}

export const getShareholderHolderFromSearch = (search: string) => {
    const params = new URLSearchParams(search)
    const holderName = params.get(SHAREHOLDER_HOLDER_QUERY_PARAM)?.trim() ?? ''
    return {
        holderName,
        exactHolderMatch: params.get(SHAREHOLDER_HOLDER_EXACT_QUERY_PARAM) === '1',
        isOpen: holderName.length > 0,
    }
}

export type ShareholderPortfolioRow = {
    exchange: Exchange
    symbol: string
    holderName: string
    hierarchyPath: string
    filingDate: string
    periodLabel: string
    periodRank: number
    shares: number
    percentage: number
    pledgedPercentage?: number
    lockedPercentage?: number
}

type BuildShareholderPortfolioRowsOptions = {
    exactHolderMatch?: boolean
}

const matchesHolderQuery = (rowHolderName: string, normalizedQuery: string, exactHolderMatch: boolean): boolean => {
    const normalizedRowName = rowHolderName.trim().toLowerCase()
    if (!normalizedQuery) {
        return false
    }
    return exactHolderMatch ? normalizedRowName === normalizedQuery : normalizedRowName.includes(normalizedQuery)
}

export const buildShareholderHoldingsSearch = (
    holderName: string,
    options?: { exact?: boolean; section?: ShareholderPortfolioSection; existingSearch?: string },
): string => {
    const params = new URLSearchParams(options?.existingSearch ?? '')
    params.delete('name')
    params.set(SHAREHOLDER_HOLDER_QUERY_PARAM, holderName.trim())
    if (options?.exact) {
        params.set(SHAREHOLDER_HOLDER_EXACT_QUERY_PARAM, '1')
    } else {
        params.delete(SHAREHOLDER_HOLDER_EXACT_QUERY_PARAM)
    }
    if (options?.section && options.section !== 'shareholding') {
        params.set(SHAREHOLDER_PORTFOLIO_SECTION_PARAM, options.section)
    } else {
        params.delete(SHAREHOLDER_PORTFOLIO_SECTION_PARAM)
    }
    return params.toString()
}

export const buildShareholderHoldingsPath = (
    holderName: string,
    options?: { exact?: boolean; section?: ShareholderPortfolioSection },
): { pathname: string; search: string } => ({
    pathname: ROUTES.shareholderHoldings,
    search: buildShareholderHoldingsSearch(holderName, options),
})

export const countShareholderSearchResultRows = (results: ShareholdingDetail[]): number =>
    results.reduce((count, detail) => count + (detail.rows?.length ?? 0), 0)

export const isShareholderSearchTruncated = (results: ShareholdingDetail[]): boolean =>
    countShareholderSearchResultRows(results) >= SHAREHOLDER_SEARCH_ROW_LIMIT

export const buildShareholderPortfolioRows = (
    results: ShareholdingDetail[],
    holderName: string,
    options?: BuildShareholderPortfolioRowsOptions,
): ShareholderPortfolioRow[] => {
    const normalizedHolder = holderName.trim().toLowerCase()
    if (!normalizedHolder) {
        return []
    }

    const exactHolderMatch = options?.exactHolderMatch ?? false
    const byStockQuarter = new Map<string, ShareholderPortfolioRow>()

    results.forEach((detail) => {
        ;(detail.rows ?? []).forEach((row) => {
            if (!matchesHolderQuery(row.holderName, normalizedHolder, exactHolderMatch)) {
                return
            }
            if (row.shareholdingPercentage <= 0 && row.shares <= 0) {
                return
            }

            const key = `${detail.exchange}:${detail.symbol}:${detail.filingDate}:${row.holderName}`
            const candidate: ShareholderPortfolioRow = {
                exchange: exchangeFromJSON(detail.exchange ?? 0),
                symbol: detail.symbol,
                holderName: row.holderName,
                hierarchyPath: row.hierarchyPath,
                filingDate: detail.filingDate,
                periodLabel: formatShareholdingPeriodLabel(detail.filingDate),
                periodRank: detail.periodRank,
                shares: row.shares,
                percentage: row.shareholdingPercentage,
                pledgedPercentage: row.pledgedPercentage,
                lockedPercentage: row.lockedPercentage,
            }

            const existing = byStockQuarter.get(key)
            if (!existing || candidate.shares > existing.shares) {
                byStockQuarter.set(key, candidate)
            }
        })
    })

    return [...byStockQuarter.values()].sort((a, b) => b.percentage - a.percentage)
}

export const getShareholderPortfolioSummary = (
    rows: ShareholderPortfolioRow[],
    searchQuery?: string,
    periodLabelOverride?: string,
) => {
    const uniqueSymbols = new Set(rows.map((row) => `${row.exchange}:${row.symbol}`))
    const uniqueHolders = new Set(rows.map((row) => row.holderName.trim().toLowerCase()))
    const latestPeriodLabel = periodLabelOverride ?? rows[0]?.periodLabel
    const singleHolderName = uniqueHolders.size === 1 ? rows[0]?.holderName : undefined
    return {
        stockCount: uniqueSymbols.size,
        holdingCount: rows.length,
        holderCount: uniqueHolders.size,
        periodLabel: latestPeriodLabel,
        displayName: singleHolderName
            ? formatHolderDisplayName(singleHolderName)
            : searchQuery
              ? formatHolderDisplayName(searchQuery)
              : '',
    }
}
